import { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import {
    DEPARTMENTS,
    COLLECTIONS,
    MIN_INDEXABLE,
    homeOf,
    stylesOf,
    collectionMatches,
    pathOfStyle,
    pathOfCollection,
} from "../Data/taxonomy";
import "./style_pages.css";

// Copy for every style and collection page on the website. Each page shows
// how many published products it has and whether Google can see it yet
// (MIN_INDEXABLE or more). The text saved here overrides the defaults in the
// shared taxonomy file; empty fields fall back to them.
const COL = "StylePages";
const docIdFor = (kind, dept, slug) => (kind === "collection" ? `collection__${slug}` : `${dept}__${slug}`);
const isLive = (p) => !p.status || p.status === "published";

const blankFaq = () => ({ q: "", a: "" });

export default function StylePages() {
    const [products, setProducts] = useState(null);
    const [saved, setSaved] = useState({});
    const [open, setOpen] = useState(null); // { kind, dept, page }
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState("");

    useEffect(() => {
        let live = true;
        Promise.all([db.collection("Presets").get(), db.collection(COL).get()])
            .then(([ps, sp]) => {
                if (!live) return;
                setProducts(ps.docs.map((d) => ({ ...d.data(), _id: d.id })).filter(isLive));
                const m = {};
                sp.forEach((d) => { m[d.id] = d.data(); });
                setSaved(m);
            })
            .catch((e) => setNote(`Could not load: ${e.message}`));
        return () => { live = false; };
    }, []);

    // product counts per page; filter-only styles count toward their parent
    const counts = useMemo(() => {
        const c = {};
        const unplaced = [];
        (products || []).forEach((p) => {
            const home = homeOf(p);
            if (!home) { unplaced.push(p); return; }
            const pages = new Set([`${home.department.slug}__${home.style.slug}`]);
            stylesOf(p).filter((s) => s.page).forEach((s) => pages.add(`${s.department}__${s.style}`));
            pages.forEach((k) => { c[k] = (c[k] || 0) + 1; });
            COLLECTIONS.forEach((col) => {
                if (collectionMatches(col, p)) c[`collection__${col.slug}`] = (c[`collection__${col.slug}`] || 0) + 1;
            });
        });
        return { c, unplaced };
    }, [products]);

    const startEdit = (kind, dept, page) => {
        const id = docIdFor(kind, dept, page.slug);
        const s = saved[id] || {};
        setOpen({ kind, dept, page, id });
        setDraft({
            title: s.title || "",
            h1: s.h1 || "",
            intro: s.intro || "",
            seoText: s.seoText || "",
            faqs: Array.isArray(s.faqs) && s.faqs.length ? s.faqs : [blankFaq()],
        });
        setNote("");
    };

    const save = async () => {
        if (!open || !draft) return;
        setBusy(true);
        const data = {
            title: draft.title.trim(),
            h1: draft.h1.trim(),
            intro: draft.intro.trim(),
            seoText: draft.seoText.trim(),
            faqs: draft.faqs.map((f) => ({ q: f.q.trim(), a: f.a.trim() })).filter((f) => f.q && f.a),
            department: open.kind === "collection" ? "" : open.dept,
            slug: open.page.slug,
            kind: open.kind,
            updatedAt: new Date(),
        };
        try {
            await db.collection(COL).doc(open.id).set(data, { merge: true });
            setSaved((prev) => ({ ...prev, [open.id]: data }));
            setNote("Saved. The website picks it up on the next page load.");
        } catch (e) {
            setNote(`Not saved: ${e.message}`);
        } finally {
            setBusy(false);
        }
    };

    const row = (kind, dept, page, url) => {
        const id = docIdFor(kind, dept, page.slug);
        const n = counts.c[id] || 0;
        const live = n >= MIN_INDEXABLE;
        const edited = !!saved[id];
        return (
            <button type="button" key={id} className={`sp-row ${open && open.id === id ? "is-open" : ""}`} onClick={() => startEdit(kind, dept, page)}>
                <span className="sp-row__name">
                    {page.label}
                    {page.priority ? <em className={`sp-p sp-p${page.priority}`}>P{page.priority}</em> : null}
                </span>
                <code className="sp-row__url">{url}</code>
                <span className="sp-row__count">{products ? n : "…"} {n === 1 ? "product" : "products"}</span>
                <span className={`sp-badge ${live ? "is-live" : ""}`}>{live ? "In Google" : `Hidden (needs ${MIN_INDEXABLE})`}</span>
                <span className={`sp-badge ${edited ? "is-edited" : "is-default"}`}>{edited ? "Custom text" : "Default text"}</span>
            </button>
        );
    };

    const defaults = open ? open.page : null;

    return (
        <div className="sp">
            <p className="sp-lead">
                Every page the website builds from the catalogue. A page goes into Google once it has {MIN_INDEXABLE} or more published
                products; until then shoppers can still open it. Click a page to write its title, intro, SEO text and FAQs.
            </p>
            {note && <p className="sp-note">{note}</p>}
            {counts.unplaced.length > 0 && (
                <div className="sp-warn">
                    <b>{counts.unplaced.length} published product{counts.unplaced.length === 1 ? "" : "s"} not on any page:</b>{" "}
                    {counts.unplaced.slice(0, 8).map((p) => `${p.name || p._id} (${p.category || p.accessoryType || "no category"})`).join(", ")}
                    {counts.unplaced.length > 8 ? "…" : ""}. Open them in View Product and pick a category.
                </div>
            )}

            <div className="sp-layout">
                <div className="sp-list">
                    {DEPARTMENTS.map((d) => (
                        <section key={d.slug} className="sp-group">
                            <h3>{d.label} <code>/products/{d.slug}</code></h3>
                            {d.styles.filter((s) => s.page).map((s) => row("style", d.slug, s, pathOfStyle(d.slug, s.slug)))}
                        </section>
                    ))}
                    <section className="sp-group">
                        <h3>Occasion collections</h3>
                        {COLLECTIONS.map((c) => row("collection", "", c, pathOfCollection(c.slug)))}
                    </section>
                </div>

                <div className="sp-editor">
                    {!open ? (
                        <p className="sp-empty">Pick a page on the left.</p>
                    ) : (
                        <>
                            <h3>{open.page.label}</h3>
                            <code className="sp-row__url">
                                maviinci.in{open.kind === "collection" ? pathOfCollection(open.page.slug) : pathOfStyle(open.dept, open.page.slug)}
                            </code>
                            <label>Page title (Google, browser tab) <small>{draft.title.length}/60</small>
                                <input value={draft.title} placeholder={defaults.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                            </label>
                            <label>Main heading (H1)
                                <input value={draft.h1} placeholder={defaults.h1} onChange={(e) => setDraft({ ...draft, h1: e.target.value })} />
                            </label>
                            <label>Intro (shown above the products) <small>{draft.intro.length}/220</small>
                                <textarea rows={3} value={draft.intro} placeholder={defaults.intro} onChange={(e) => setDraft({ ...draft, intro: e.target.value })} />
                            </label>
                            <label>SEO text (shown below the products; 150–300 words works well)
                                <textarea rows={8} value={draft.seoText} placeholder="Fabrics, occasions, how to choose, how made-to-measure works…" onChange={(e) => setDraft({ ...draft, seoText: e.target.value })} />
                            </label>
                            <div className="sp-faqs">
                                <span>FAQs</span>
                                {draft.faqs.map((f, i) => (
                                    <div className="sp-faq" key={i}>
                                        <input placeholder="Question" value={f.q} onChange={(e) => setDraft({ ...draft, faqs: draft.faqs.map((x, j) => (j === i ? { ...x, q: e.target.value } : x)) })} />
                                        <textarea rows={2} placeholder="Answer" value={f.a} onChange={(e) => setDraft({ ...draft, faqs: draft.faqs.map((x, j) => (j === i ? { ...x, a: e.target.value } : x)) })} />
                                        <button type="button" className="sp-link" onClick={() => setDraft({ ...draft, faqs: draft.faqs.filter((_, j) => j !== i) })}>Remove</button>
                                    </div>
                                ))}
                                <button type="button" className="sp-link" onClick={() => setDraft({ ...draft, faqs: [...draft.faqs, blankFaq()] })}>+ Add a question</button>
                            </div>
                            <div className="sp-actions">
                                <button type="button" className="sp-save" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save page text"}</button>
                                <span className="sp-hint">Empty fields use the default text shown in grey.</span>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
